import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useProfile } from '../hooks/useProfile';

const C = {
  navy: '#0A1628', gold: '#C9A84C', black: '#0A0A0A',
  white: '#FFFFFF', surface: '#F5F6F8', border: '#D0D5DD',
  borderLt: '#E8EBF0', text: '#0A0A0A', textSec: '#3D4552',
  muted: '#6B7280', mutedLt: '#9CA3AF',
  success: '#0A7C42', danger: '#C0222A',
};

interface Connection {
  id: string; status: string; requested_at: string;
  allocator_firm: { id: string; name: string; type: string; headquarters: string; aum_min: number; aum_max: number; description: string; website: string; };
  requested_by_profile: { full_name: string; email: string; };
}

const card: React.CSSProperties = {
  background: C.white, border: `1px solid ${C.border}`,
  borderRadius: '4px', padding: '18px 20px', marginBottom: '12px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};

export default function AllocatorProfile() {
  const { connectionId } = useParams();
  const navigate = useNavigate();
  const { firm } = useProfile();
  const [connection, setConnection] = useState<Connection | null>(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  useEffect(() => { fetchConnection(); }, [connectionId]);

  const fetchConnection = async () => {
    const { data } = await supabase.from('connections')
      .select(`*, allocator_firm:firms!connections_allocator_firm_id_fkey(id, name, type, headquarters, aum_min, aum_max, description, website), requested_by_profile:profiles!connections_requested_by_fkey(full_name, email)`)
      .eq('id', connectionId).single();
    if (data) setConnection(data);
    setLoading(false);
  };

  const handleAction = async (status: 'approved' | 'declined') => {
    if (!connection) return;
    setActing(true);
    await supabase.from('connections').update({ status, responded_at: new Date().toISOString() }).eq('id', connection.id);
    try {
      await supabase.functions.invoke('send-email-notification', {
        body: { type: status === 'approved' ? 'access_approved' : 'access_declined', data: { allocator_email: connection.requested_by_profile?.email, allocator_name: connection.requested_by_profile?.full_name || 'there', fund_name: `all funds at ${firm?.name}`, manager_firm_name: firm?.name || 'the manager' } },
      });
    } catch (err) { console.error('Email failed:', err); }
    setActing(false);
    navigate('/manage-investors');
  };

  if (loading) return (
    <div style={{ padding: '32px', background: C.surface, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: C.muted, fontSize: '13px', fontFamily: 'system-ui' }}>Loading...</div>
    </div>
  );

  if (!connection) return (
    <div style={{ padding: '32px', background: C.surface, minHeight: '100vh', fontFamily: 'system-ui' }}>
      <div style={{ color: C.danger, fontSize: '13px' }}>Connection not found.</div>
    </div>
  );

  const af = Array.isArray(connection.allocator_firm) ? connection.allocator_firm[0] : connection.allocator_firm;

  return (
    <div style={{ padding: '28px 32px', background: C.surface, minHeight: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      <button onClick={() => navigate('/manage-investors')} style={{
        background: C.white, border: `1px solid ${C.border}`, borderRadius: '3px',
        color: C.textSec, padding: '5px 12px', cursor: 'pointer',
        fontSize: '12px', marginBottom: '20px', fontFamily: 'system-ui', fontWeight: 500,
      }}>← Back to Manage Investors</button>

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '22px', paddingBottom: '18px', borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{
            width: '44px', height: '44px', borderRadius: '4px',
            background: C.navy, display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: '18px', fontWeight: 700,
            color: C.gold, fontFamily: 'Georgia, serif', flexShrink: 0,
          }}>
            {af?.name?.charAt(0) || '?'}
          </div>
          <div>
            <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '22px', color: C.black, fontWeight: 400, margin: '0 0 3px', letterSpacing: '-0.3px' }}>
              {af?.name || 'Unknown firm'}
            </h1>
            <div style={{ fontSize: '12px', color: C.muted }}>
              Requested by {connection.requested_by_profile?.full_name} · {new Date(connection.requested_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          {connection.status === 'pending' ? (
            <>
              <button onClick={() => handleAction('approved')} disabled={acting} style={{
                padding: '8px 18px', borderRadius: '3px', fontSize: '12.5px', fontWeight: 600,
                cursor: acting ? 'not-allowed' : 'pointer', fontFamily: 'system-ui',
                border: 'none', background: C.navy, color: C.white,
                opacity: acting ? 0.7 : 1,
              }}>Approve</button>
              <button onClick={() => handleAction('declined')} disabled={acting} style={{
                padding: '8px 18px', borderRadius: '3px', fontSize: '12.5px', fontWeight: 600,
                cursor: acting ? 'not-allowed' : 'pointer', fontFamily: 'system-ui',
                border: `1px solid ${C.danger}`, background: C.white, color: C.danger,
                opacity: acting ? 0.7 : 1,
              }}>Decline</button>
            </>
          ) : (
            <span style={{
              fontSize: '10px', fontWeight: 700, padding: '4px 12px', borderRadius: '2px',
              textTransform: 'uppercase', letterSpacing: '0.5px',
              background: connection.status === 'approved' ? '#F0FDF4' : '#FEF2F2',
              color: connection.status === 'approved' ? C.success : C.danger,
              border: `1px solid ${connection.status === 'approved' ? '#BBF7D0' : '#FECACA'}`,
            }}>{connection.status}</span>
          )}
        </div>
      </div>

      {/* ── Firm details ── */}
      <div style={card}>
        <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '14px' }}>
          Firm Details
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: af?.website ? '14px' : '0' }}>
          {[
            { label: 'Type',         value: af?.type ? af.type.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) : '-' },
            { label: 'Headquarters', value: af?.headquarters || '-' },
            { label: 'AUM',          value: af?.aum_min ? `$${af.aum_min}–${af.aum_max}mm` : '-' },
            { label: 'Contact',      value: connection.requested_by_profile?.email || '-' },
          ].map((item, i) => (
            <div key={i}>
              <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '4px' }}>{item.label}</div>
              <div style={{ fontSize: '13px', fontWeight: 500, color: C.text, fontFamily: i === 2 ? '"Courier New", monospace' : 'system-ui' }}>{item.value}</div>
            </div>
          ))}
        </div>
        {af?.website && (
          <div style={{ borderTop: `1px solid ${C.borderLt}`, paddingTop: '12px' }}>
            <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '4px' }}>Website</div>
            <a href={af.website} target="_blank" rel="noreferrer" style={{ fontSize: '13px', color: C.navy, textDecoration: 'underline', textDecorationStyle: 'dotted' }}>
              {af.website}
            </a>
          </div>
        )}
      </div>

      {/* ── About ── */}
      {af?.description && (
        <div style={card}>
          <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '10px' }}>About</div>
          <p style={{ fontSize: '13px', color: C.textSec, lineHeight: 1.7, margin: 0 }}>{af.description}</p>
        </div>
      )}

      {/* ── Access request ── */}
      <div style={{ ...card, borderLeft: `3px solid ${C.navy}` }}>
        <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '10px' }}>Access Request</div>
        <p style={{ fontSize: '13px', color: C.textSec, lineHeight: 1.7, margin: 0 }}>
          {af?.name} is requesting access to all funds at {firm?.name}. If approved, they will be able to view full fund details, performance data, and contact you via the platform.
        </p>
      </div>
    </div>
  );
}